import createClient, { type Client, type Middleware } from "openapi-fetch";
import type { paths } from "./schema";

/**
 * El cliente tipado de la API del ERP.
 *
 * Los tipos salen del contrato OpenAPI (`schema.d.ts`, generado con
 * `pnpm api:generate`), no se escriben a mano: si el backend cambia una respuesta,
 * el error aparece al compilar y no en producción.
 *
 * El token no se guarda aquí. Se pide en cada petición a través de `getToken`, que
 * la capa de autenticación implementa: así el cliente no tiene que enterarse de
 * cuándo caduca un token ni cómo se refresca, y no hay una copia del token
 * envejeciendo dentro de esta clase.
 */
export interface ApiClientOptions {
  /** La raíz de la API, p. ej. `https://erp.farmacentercerete.com`. */
  baseUrl: string;
  /**
   * Devuelve el token vigente, o `null` si no hay sesión. Puede ser asíncrono
   * porque refrescar un token caducado es una llamada de red.
   */
  getToken: () => Promise<string | null> | string | null;
}

export type FarmacenterApi = Client<paths>;

export function createApiClient(options: ApiClientOptions): FarmacenterApi {
  const client = createClient<paths>({ baseUrl: options.baseUrl });

  const auth: Middleware = {
    async onRequest({ request }) {
      const token = await options.getToken();
      if (token) {
        request.headers.set("Authorization", `Bearer ${token}`);
      }
      return request;
    },
  };

  client.use(auth);
  return client;
}
